/*
  assets/gcitypartners-service-lane-map.js — GCITYPARTNERS-SERVICE-LANE-MAP-02
  Owner: City Mission / GIS clue / GLG service-lane mapping.

  Contract:
  - exactly eight canonical service lanes
  - strict deduplication: no self-mapping, no overlapping aliases/mission handles
  - unknown input fails closed and never silently becomes urban-renewal
  - citizen-facing service_theme is an additive projection; it never replaces service_lane

  Non-owner: Shopify product routes, checkout, order creation, PDF delivery,
  valuation conclusions or surveying conclusions.
*/
(function () {
  'use strict';

  /**
   * @typedef {{ version: string } & Record<string, unknown>}
   *   ServiceLaneAPI
   */

  /** @type {Window & typeof globalThis & {
   *   GCityPartnersServiceLaneMap?: ServiceLaneAPI
   * }} */
  var runtimeWindow = /** @type {Window & typeof globalThis & {
    GCityPartnersServiceLaneMap?: ServiceLaneAPI
  }} */ (window);

  var VERSION = '1.5.0-citizen-service-theme-crosswalk';
  if (
    runtimeWindow.GCityPartnersServiceLaneMap
    && runtimeWindow.GCityPartnersServiceLaneMap.version === VERSION
  ) return;

  /** @param {unknown} value */
  function norm(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/_/g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /** @param {readonly string[]} list */
  function uniqueArray(list) {
    var seen = Object.create(null);
    return (list || []).filter(function (item) {
      var token = norm(item);
      if (!token || seen[token]) return false;
      seen[token] = true;
      return true;
    });
  }

  /** @param {readonly string[]} value */
  function freezeArray(value) {
    return Object.freeze(uniqueArray(value));
  }

  /** @type {Record<string, string>} */
  var aliases = {
    'old-city-new-district': 'urban-renewal',
    'city-renewal': 'urban-renewal',
    'district-bug': 'urban-renewal',
    'street-bug': 'urban-renewal',
    'land-use-clue': 'land-natural-resources',
    'land-bug': 'land-natural-resources',
    'land': 'land-natural-resources',
    'asset-clue': 'property-assets-investment',
    'property-asset': 'property-assets-investment',
    'property': 'property-assets-investment',
    'engineering-bug': 'infrastructure-engineering',
    'infrastructure-bug': 'infrastructure-engineering',
    'engineering': 'infrastructure-engineering',
    'map-bug': 'digital-city',
    'smart-city': 'digital-city',
    'spatial-intelligence': 'digital-city',
    'water-bug': 'water-resilience',
    'waterfront-bug': 'water-resilience',
    'drainage': 'water-resilience',
    'water-systems': 'water-resilience',
    'water': 'water-resilience',
    'greener-city': 'green-city-service',
    'environment-bug': 'green-city-service',
    'green': 'green-city-service',
    'environment': 'green-city-service',
    'park-bug': 'industry-park-development',
    'industry': 'industry-park-development',
    'surveying-geomatics': 'infrastructure-engineering',
    'property-asset-valuation': 'property-assets-investment',
    'digital-city-geospatial-analytics': 'digital-city',
    'ecology-environmental-assessment': 'green-city-service',
    'rural-development-agriculture': 'land-natural-resources',
    'industrial-park-planning-development': 'industry-park-development',
    'planning-development': 'land-natural-resources',
    'drone-surveying-operations': 'infrastructure-engineering',
    'hydrographic-marine-surveying': 'water-resilience',
    'water-infrastructure-resilience': 'water-resilience',
    'ai-automation-solutions': 'digital-city'
  };

  /**
   * @typedef {object} ServiceLane
   * @property {string} id
   * @property {string} service_theme
   * @property {readonly string[]} mission_handles
   * @property {readonly string[]} partner_types
   * @property {readonly string[]} capability_zh
   * @property {readonly string[]} capability_en
   * @property {readonly string[]} evidence_zh
   * @property {readonly string[]} evidence_en
   * @property {string} group_scenario
   * @property {string} product_group
   * @property {string} opportunity_type
   */

  var rawLanes = [
    {
      id: 'urban-renewal',
      service_theme: 'service-housing-land',
      mission_handles: ['urban-renewal', 'urban-renewal', 'cleaner-corner', 'brighter-old-district', 'market-shop-front-blocked'],
      label: 'Urban Renewal',
      zh: '城市更新',
      formal_zh: '城市與片區發展',
      formal_en: 'Cities and District Development',
      group_scenario: '城市與片區發展',
      product_group: 'urban-planning',
      partner_types: ['urban_renewal_advisory', 'planning_advisory', 'district_operator'],
      opportunity_type: 'district_improvement_candidate',
      hint: 'old district / public space / access',
      capability_zh: ['更新基礎調查', '權屬與現狀核實', '片區統籌策劃', '價值與實施路徑研判', '一圖通管理'],
      capability_en: ['baseline survey', 'ownership and condition check', 'district planning', 'value and implementation path review', 'one-map project management'],
      evidence_zh: ['入口、路牌、公共空間、動線、停車、照明、片區更新線索'],
      evidence_en: ['access, signage, public realm, circulation, parking, lighting, district-renewal clues'],
      handoff_zh: '把市民觀察轉成城市更新初步審核草稿，再判斷是否需要現狀摸查、價值研判或片區策劃。',
      handoff_en: 'Turn the citizen observation into an urban-renewal review draft, then decide whether baseline survey, value review, or district planning is needed.'
    },
    {
      id: 'land-natural-resources',
      service_theme: 'service-housing-land',
      mission_handles: ['land-detective', 'land-natural-resources'],
      label: 'Land / Nature',
      zh: '土地與自然資源',
      formal_zh: '土地與自然資源',
      formal_en: 'Land and Natural Resources',
      group_scenario: '土地與自然資源',
      product_group: 'land-intelligence',
      partner_types: ['land_advisory', 'natural_resources_review', 'survey_mapping'],
      opportunity_type: 'land_resource_candidate',
      hint: 'boundary / path / future use',
      capability_zh: ['地籍調查', '自然資源調查', '權屬邊界核實', '用途研判', '組卷報批支撐'],
      capability_en: ['cadastral survey', 'natural-resource investigation', 'boundary and ownership verification', 'land-use review', 'application and approval support'],
      evidence_zh: ['邊界、用地、路徑、自然資源、整治、報批、確權線索'],
      evidence_en: ['boundary, land use, access path, natural-resource, consolidation, approval, ownership clues'],
      handoff_zh: '把城市線索映射到土地與自然資源場景，先形成可追溯的邊界、用途與權屬問題清單。',
      handoff_en: 'Map the city clue into the land and natural-resources lane, starting with traceable boundary, use, and ownership questions.'
    },
    {
      id: 'property-assets-investment',
      service_theme: 'service-tax-finance',
      mission_handles: ['property-life-circle', 'property-assets-investment', 'safer-campus', 'building-leakage-ac-drip'],
      label: 'Property / Assets',
      zh: '不動產與資產',
      formal_zh: '不動產、資產與投資',
      formal_en: 'Real Estate, Assets and Investment',
      group_scenario: '不動產、資產與投資',
      product_group: 'property-investment',
      partner_types: ['asset_advisory', 'property_review', 'valuation_review_candidate'],
      opportunity_type: 'asset_condition_candidate',
      hint: 'building access / common space / asset clue',
      capability_zh: ['資產盤點', '權屬核查', '房地產估價', '收益與現金流分析', 'GIS 資產管理'],
      capability_en: ['asset inventory', 'rights review', 'real estate valuation', 'income and cash-flow analysis', 'GIS asset management'],
      evidence_zh: ['樓宇、公共空間、權屬、租賃、收益、價值影響線索'],
      evidence_en: ['building, common space, rights, lease, income, value-impact clues'],
      handoff_zh: '把任務卡整理為資產與價值線索，後續才進入正式權屬核查、估值或投資研判。',
      handoff_en: 'Convert the mission card into asset and value clues before any formal rights check, valuation, or investment review.'
    },
    {
      id: 'infrastructure-engineering',
      service_theme: 'service-utilities',
      mission_handles: ['engineering-delivery', 'infrastructure-engineering', 'friendly-walk', 'commute-gate-crowd', 'green-man-crossing', 'elderly-ramp-handrail'],
      label: 'Infrastructure',
      zh: '基礎設施與工程',
      formal_zh: '基礎設施與工程',
      formal_en: 'Infrastructure and Engineering',
      group_scenario: '基礎設施與工程',
      product_group: 'survey-technical-services',
      partner_types: ['engineering_survey', 'infrastructure_operator', 'safety_access_review'],
      opportunity_type: 'infrastructure_service_candidate',
      hint: 'route / lighting / crossing',
      capability_zh: ['工程測量', '地下管線探測', '施工控制', '沉降觀測', '多測合一與竣工測繪'],
      capability_en: ['engineering survey', 'underground utility detection', 'construction control', 'settlement monitoring', 'multi-survey and as-built survey'],
      evidence_zh: ['道路、交叉口、照明、排水、管線、施工安全、竣工移交線索'],
      evidence_en: ['road, crossing, lighting, drainage, utilities, construction-safety, as-built handoff clues'],
      handoff_zh: '把現場觀察轉成工程測繪與基礎設施核查入口，不直接形成工程結論。',
      handoff_en: 'Turn the on-site observation into an engineering-survey and infrastructure-check intake, without fabricating engineering conclusions.'
    },
    {
      id: 'digital-city',
      service_theme: 'service-communications-tech',
      mission_handles: ['digital-city', 'digital-city', 'visible-culture', 'orderly-low-altitude'],
      label: 'Digital City',
      zh: '數字城市與空間智能',
      formal_zh: '數字城市與空間智能',
      formal_en: 'Digital City and Spatial Intelligence',
      group_scenario: '數字城市與空間智能',
      product_group: 'enterprise-ai-platform',
      partner_types: ['gis_platform', 'ai_spatial_system', 'low_altitude_data_partner'],
      opportunity_type: 'digital_platform_candidate',
      hint: 'map / information / service entry',
      capability_zh: ['GIS 一圖通', '實景三維', 'AI 空間分析', '低空影像接入', '業務流程集成'],
      capability_en: ['GIS one-map', 'reality 3D', 'AI spatial analysis', 'low-altitude imagery ingestion', 'workflow integration'],
      evidence_zh: ['地圖圖層、三維模型、低空影像、數據點、服務入口、平台流程線索'],
      evidence_en: ['map layer, 3D model, low-altitude imagery, data point, service entry, platform workflow clues'],
      handoff_zh: '把城市線索轉成 GIS / 3D / data layer 的平台化需求，再判斷是否進入數字城市產品或 API 服務。',
      handoff_en: 'Translate the city clue into GIS / 3D / data-layer requirements, then decide whether it belongs in a digital-city product or API service.'
    },
    {
      id: 'water-resilience',
      service_theme: 'service-utilities',
      mission_handles: ['water-resilience', 'water-marine-environments', 'safer-waterfront', 'rainwater-mosquito-spot', 'extreme-weather-home-route'],
      label: 'Water / Resilience',
      zh: '水務與韌性',
      formal_zh: '水資源與海洋環境',
      formal_en: 'Water Resources and Marine Environment',
      group_scenario: '水資源與海洋環境',
      product_group: 'sustainability',
      partner_types: ['water_resilience_review', 'marine_survey', 'environmental_monitoring'],
      opportunity_type: 'water_resilience_candidate',
      hint: 'drainage / water edge / rain route',
      capability_zh: ['河湖岸線測繪', '水深測量', '排水與雨洪線索核查', '水域測繪', '常態化監測'],
      capability_en: ['river-lake shoreline survey', 'bathymetric survey', 'drainage and stormwater clue check', 'marine survey', 'continuous monitoring'],
      evidence_zh: ['排水、水邊空間、雨天路線、河湖岸線、水下地形、風險點線索'],
      evidence_en: ['drainage, waterfront, rainy route, shoreline, underwater terrain, risk-point clues'],
      handoff_zh: '把雨天與水邊觀察整理為水資源或韌性治理線索，後續才判斷測繪、監測或治理方案。',
      handoff_en: 'Organize rain-route and waterfront observations into water-resilience clues before deciding on survey, monitoring, or governance work.'
    },
    {
      id: 'green-city-service',
      service_theme: 'service-housing-land',
      mission_handles: ['green-city', 'green-city-service', 'environment-sustainability', 'greener-comfort', 'cooler-green-route', 'estate-recycling-room', 'public-toilet-cleanliness', 'park-playground-pet-zone'],
      label: 'Green / Sustainability',
      zh: '生態與可持續',
      formal_zh: '生態環境與可持續發展',
      formal_en: 'Ecological Environment and Sustainable Development',
      group_scenario: '生態環境與可持續發展',
      product_group: 'sustainability',
      partner_types: ['sustainability_advisory', 'green_city_operator', 'environmental_service'],
      opportunity_type: 'green_city_service_candidate',
      hint: 'shade / seating / comfort',
      capability_zh: ['生態本底摸排', '環境動態監測', '綠色空間舒適度分析', 'ESG 成果梳理', '修復方案支撐'],
      capability_en: ['ecological baseline survey', 'environmental monitoring', 'green-space comfort analysis', 'ESG evidence summary', 'restoration-plan support'],
      evidence_zh: ['遮陰、熱點、綠化、公共舒適、可持續、修復或環境問題線索'],
      evidence_en: ['shade, heat, greenery, public comfort, sustainability, restoration or environmental issue clues'],
      handoff_zh: '把 +0.01% 的公共舒適觀察轉成生態環境與可持續的初步線索。',
      handoff_en: 'Turn the +0.01% public-comfort observation into an ecological and sustainability clue.'
    },
    {
      id: 'industry-park-development',
      service_theme: 'service-trade-commerce',
      mission_handles: ['industry-park', 'industry-park-development'],
      label: 'Industry Park',
      zh: '產業園區',
      formal_zh: '產業園區發展',
      formal_en: 'Industrial Park Development',
      group_scenario: '產業園區發展',
      product_group: 'enterprise-ai-platform',
      partner_types: ['industrial_park_operator', 'smart_park_platform', 'industry_research'],
      opportunity_type: 'industrial_park_candidate',
      hint: 'entry / circulation / signage',
      capability_zh: ['園區自然資源調查', '產業研究', '空間規劃', '實施評估', '智慧園區運營'],
      capability_en: ['park resource survey', 'industry research', 'spatial planning', 'implementation evaluation', 'smart-park operations'],
      evidence_zh: ['園區入口、動線、標識、服務節點、產業空間、智慧園區線索'],
      evidence_en: ['park entrance, circulation, signage, service node, industrial space, smart-park clues'],
      handoff_zh: '把園區觀察映射到產業空間和園區運營服務，先形成專業 intake，不直接承諾改造結果。',
      handoff_en: 'Map the park observation to industrial-space and park-operations service intake, without promising redevelopment outcomes.'
    }
  ];

  /** @param {ServiceLane} lane */
  function freezeLane(lane) {
    /** @type {Array<
     * 'mission_handles' | 'partner_types' |
     * 'capability_zh' | 'capability_en' |
     * 'evidence_zh' | 'evidence_en'
     * >} */
    var arrayKeys = [
      'mission_handles',
      'partner_types',
      'capability_zh',
      'capability_en',
      'evidence_zh',
      'evidence_en'
    ];
    arrayKeys.forEach(function (key) {
      lane[key] = freezeArray(lane[key]);
    });
    return Object.freeze(lane);
  }

  var lanes = rawLanes.map(freezeLane);
  var byId = Object.create(null);
  var missionToLane = Object.create(null);

  lanes.forEach(function (lane) {
    byId[lane.id] = lane;
    lane.mission_handles.forEach(function (handle) {
      var token = norm(handle);
      if (token) missionToLane[token] = lane.id;
    });
  });

  var cleanAliases = Object.create(null);
  Object.keys(aliases).forEach(function (key) {
    var normalizedKey = norm(key);
    var target = norm(aliases[key]);
    if (normalizedKey && byId[target] && normalizedKey !== target && !missionToLane[normalizedKey]) {
      cleanAliases[normalizedKey] = target;
    }
  });

  aliases = Object.freeze(cleanAliases);
  Object.freeze(lanes);

  /** @param {unknown} value */
  function canonical(value) {
    var key = norm(value);
    if (!key) return '';
    if (byId[key]) return key;
    if (aliases[key]) return aliases[key];
    if (missionToLane[key]) return missionToLane[key];
    return '';
  }

  /** @param {unknown} id */
  function findLane(id) {
    var key = canonical(id);
    return key ? byId[key] || null : null;
  }

  /** @param {unknown} id */
  function serviceTheme(id) {
    var lane = findLane(id);
    return lane ? lane.service_theme || '' : '';
  }

  /** @param {unknown} id */
  function crosswalk(id) {
    var lane = findLane(id);
    if (!lane) {
      return {
        service_lane: '',
        service_theme: '',
        group_scenario: '',
        product_group: '',
        partner_types: [],
        opportunity_type: 'city_issue_review',
        formal_conclusion: false,
        valuation_conclusion: false,
        surveying_conclusion: false,
        mapping_status: 'unmapped'
      };
    }

    return {
      service_lane: lane.id,
      service_theme: lane.service_theme || '',
      group_scenario: lane.group_scenario,
      product_group: lane.product_group,
      partner_types: lane.partner_types.slice(),
      opportunity_type: lane.opportunity_type || 'city_issue_review',
      formal_conclusion: false,
      valuation_conclusion: false,
      surveying_conclusion: false,
      mapping_status: 'candidate_only'
    };
  }

  runtimeWindow.GCityPartnersServiceLaneMap = Object.freeze({
    version: VERSION,
    owner: 'gcitypartners-service-lane-map',
    patch18_unit: '18-19',
    aliases: aliases,
    conversion_loop: freezeArray([
      'citizen-participation',
      'gis-context',
      'draft-organization',
      'service-lane-candidate',
      'citizen-service-theme-projection',
      'product-group-candidate',
      'partner-type-candidate',
      'review-boundary'
    ]),
    lanes: lanes,
    canonical: canonical,
    find: findLane,
    serviceTheme: serviceTheme,
    crosswalk: crosswalk,
    /**
     * @param {Record<string, unknown> | null | undefined} input
     */
    fromCityIssue: function (input) {
      /** @type {Record<string, unknown>} */
      var source = input && typeof input === 'object' ? input : {};
      return crosswalk(
        source.service_lane
        || source.scene
        || source.scenario
        || source.mission_handle
        || source.lead_type
        || ''
      );
    }
  });
})();
